/**
 * sparePartsRules.js - the Spare Parts screens' pure helpers (tested in
 * __tests__/sparePartsRules.test.js). The price rules themselves - the
 * green / yellow / red age, the discount, the stamp - are the server's
 * (server/lib/spareParts.js): every part arrives with its price_age and
 * discount_pct worked out, so the screens only show them.
 *
 *   BAND_STYLE            the chip per price band (GREEN 0-3 months, YELLOW
 *                         4-8, RED 9+ "Get prices checked", grey none)
 *   fmtMoney / fmtPct / fmtDay   how a price, a discount and a day read
 *   sortRows              the catalog's column sort (blanks always last)
 *   parseGrid / parseText a pasted or uploaded FANUC recommended spare parts
 *                         list -> lines { part_number, description,
 *                         recommended_qty, category } for POST /import
 *   projectLabel          "Q5300 · Lucid · KSA robots" for a project picker
 */
import { signedMoney } from '../../utils/format';

export const BANDS = ['green', 'yellow', 'red', 'none'];
export const BAND_STYLE = {
  green: { label: 'Current', bg: '#E8F5E9', color: '#1B5E20', dot: '#2E7D32', hint: 'Checked in the last 3 months' },
  yellow: { label: 'Aging', bg: '#FFF8E1', color: '#8D6E00', dot: '#F9A825', hint: 'Checked 4 to 8 months ago' },
  red: {
    label: 'Get prices checked',
    bg: '#FFEBEE',
    color: '#B71C1C',
    dot: '#C62828',
    hint: '9 months or more since the price was checked',
  },
  none: { label: 'No price yet', bg: '#F1F3F4', color: '#5F6368', dot: '#9AA0A6', hint: 'No STANDARD or GLOBAL price entered' },
};

const blank = (v) => v === null || v === undefined || v === '';

/** $1,234.50; a dash when there is none. */
export function fmtMoney(v, currency = 'USD') {
  if (blank(v) || !Number.isFinite(Number(v))) return '-';
  const n = Number(v);
  const sym = currency === 'USD' ? '$' : `${currency} `;
  return signedMoney(n, sym);
}
/** 35.00%; a dash when there is none. */
export const fmtPct = (v) => (blank(v) || !Number.isFinite(Number(v)) ? '-' : `${Number(v).toFixed(2)}%`);
/** 2026-10-08 -> Oct 8, 2026 (the day as stored, no time zone shift). */
export function fmtDay(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  if (!m) return '-';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const BAND_ORDER = { red: 0, yellow: 1, green: 2, none: 3 };
const SORT_VALUE = {
  price_age: (r) => (r.price_age ? BAND_ORDER[r.price_age.band] : null),
  price_checked_at: (r) => r.price_checked_at || null,
};

/**
 * Rows sorted by a column, `dir` 'asc' | 'desc'. Numbers sort as numbers,
 * text without case; an empty cell always sorts last either way.
 */
export function sortRows(rows, key, dir = 'asc') {
  if (!key) return rows;
  const get = SORT_VALUE[key] || ((r) => r[key]);
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    if (blank(x) && blank(y)) return 0;
    if (blank(x)) return 1;
    if (blank(y)) return -1;
    const nx = typeof x === 'number' ? x : Number.NaN;
    const ny = typeof y === 'number' ? y : Number.NaN;
    if (!Number.isNaN(nx) && !Number.isNaN(ny)) return (nx - ny) * sign;
    return String(x).localeCompare(String(y), 'en', { sensitivity: 'base', numeric: true }) * sign;
  });
}

const HEADS = [
  ['part_number', /part\s*(no|num|#)|^part$|p\/n|item\s*(no|num|#)|spec/i],
  ['description', /desc|name/i],
  ['recommended_qty', /qty|quan|q'ty|recommended/i],
  ['category', /cat|group|section|level|class/i],
];

/** A cell as one line of text. */
const cellText = (v) => (v === null || v === undefined ? '' : String(v).replace(/ /g, ' ').trim());
const looksLikeQty = (v) => /^\d{1,4}$/.test(cellText(v));
const looksLikePart = (v) => /^[A-Za-z0-9][A-Za-z0-9#/.-]{3,}$/.test(cellText(v).replace(/\s+/g, ''));

/**
 * A grid of cells (rows of columns: a sheet, or pasted text) -> the list's
 * lines. A header row is found by its titles (PART NO / DESCRIPTION / QTY /
 * CATEGORY ...); without one, the first column is the part number, the next
 * text column the description and a whole-number column the quantity.
 * -> { lines, skipped } (skipped: rows with no part number in them)
 */
export function parseGrid(grid) {
  const rows = (grid || []).map((r) => (Array.isArray(r) ? r : [r]));
  let headAt = -1;
  let cols = null;
  for (let i = 0; i < Math.min(rows.length, 15) && headAt < 0; i += 1) {
    const found = {};
    rows[i].forEach((c, j) => {
      const t = cellText(c);
      for (const [key, re] of HEADS)
        if (!(key in found) && t && t.length < 40 && re.test(t)) {
          found[key] = j;
          break;
        }
    });
    if ('part_number' in found && ('recommended_qty' in found || 'description' in found)) {
      headAt = i;
      cols = found;
    }
  }
  const lines = [];
  let skipped = 0;
  rows.slice(headAt + 1).forEach((r) => {
    if (!r.some((c) => cellText(c))) return;
    let pn;
    let desc;
    let qty;
    let cat;
    if (cols) {
      pn = cellText(r[cols.part_number]);
      desc = 'description' in cols ? cellText(r[cols.description]) : '';
      qty = 'recommended_qty' in cols ? cellText(r[cols.recommended_qty]) : '';
      cat = 'category' in cols ? cellText(r[cols.category]) : '';
    } else {
      pn = cellText(r[0]);
      const rest = r.slice(1).map(cellText);
      qty = rest.find(looksLikeQty) || '';
      desc = rest.find((c) => c && !looksLikeQty(c)) || '';
      cat = '';
    }
    if (!pn || !looksLikePart(pn)) {
      skipped += 1;
      return;
    }
    const n = Number(qty);
    lines.push({
      part_number: pn,
      description: desc || null,
      recommended_qty: Number.isInteger(n) && n > 0 ? n : 1,
      category: cat || null,
    });
  });
  return { lines, skipped };
}

/** One CSV line into cells ("a, ""b""",c handled). */
function csvCells(line) {
  const out = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/** Text pasted from Excel (tabs) or a CSV -> parseGrid. */
export function parseText(text) {
  const lines = String(text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const tabs = lines.some((l) => l.includes('\t'));
  return parseGrid(lines.map((l) => (tabs ? l.split('\t') : csvCells(l))));
}

/** "Q5300 · Lucid · KSA robots" (camelCase AppContext rows or snake_case API rows). */
export function projectLabel(p) {
  if (!p) return '';
  const q = p.quoteLetter || p.quote_letter || p.project_quote;
  const cust = p.customer || p.project_customer;
  const name = p.name || p.project_name;
  return [q ? `Q${q}` : null, cust, name].filter(Boolean).join(' · ');
}
